import React, { useState } from 'react';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { Avatar, List, Tag, Spin, Tooltip, Modal, Popover, notification } from 'antd';
import classNames from 'classnames';
import moment from 'moment';
import Linkify from 'react-linkify';
import styles from './NoticeList.less';
import RenderDot from './RenderDot';
import { connect } from 'dva';
import _ from 'lodash';
import { defaultInventoryFilters } from '@/pages/Monitoring/Inventory/models/Inventory';
import { defaultRiskFilters } from '@/models/Risks';
import { formatDateAndTime, redirectToScreen } from '@/utils/utils';
import { router } from 'umi';

export const notificationTypes = {
  cloudxray: {
    start: 'cloudxray-start',
    completed: 'cloudxray-completed',
    deleteStack: 'delete cloudxray scanning instances',
    cleanupSuccessful: 'force cleanup ended successfully',
  },
};

const NoticeList = ({
  data = [],
  onClick,
  onClear,
  clickNotification,
  title,
  locale,
  loadingMore,
  lastVisible,
  emptyText,
  emptyImage,
  onViewMore = null,
  showClear = false,
  showViewMore = false,
  allCloudAccountsList,
  ...props
}) => {
  const [isDetailsModalVisible, setIsDetailsModalVisible] = useState(false);
  const [currentModalItem, setCurrentModalItem] = useState({});

  if (data.length === 0) {
    return (
      <div className={styles.notFound}>
        {emptyImage ? <img src={emptyImage} alt="not found" /> : null}
        <div>{emptyText || locale.emptyText}</div>
      </div>
    );
  }

  const handleInfiniteOnLoad = () => {
    if (lastVisible) clickNotification(false, 'moreList');
  };

  const handleCancel = type => {
    if (type) window.open('https://cloudnosys.com/docs/troubleshoot-cloudxray/', '_blank');
    else Modal.destroyAll();
  };

  const handleItemClick = async item => {
    const orgId = item.organization.id;
    const { cloudAccountId } = item.organization;
    if (item?.title === 'permission') {
      setIsDetailsModalVisible(true);
      setCurrentModalItem(item);
      return;
    }

    if (item.title?.toLowerCase().includes('cloudxray')) {
      // * 1 handle cloudXray notification navigations
      const notifOfCloudAccount = allCloudAccountsList.find(each => each.id === cloudAccountId);
      // ? If the account is not available then show notification to user about cloud account not present
      if (_.isEmpty(notifOfCloudAccount)) {
        notification.error({
          message: 'This cloud account is not present in your organization anymore.',
          placement: 'bottomRight',
        });
        return;
      }
      if (!notifOfCloudAccount.isActive) {
        notification.warning({
          message: 'This cloud account is currently inactive in your organization.',
          placement: 'bottomRight',
        });
        return;
      }

      const {
        description,
        cloudAccountName,
        organization: { name: orgName },
        notificationContent = null,
        provider,
      } = item || {};

      const modalWidth =
        (notificationContent?.failedInstances?.length > 0 ||
          notificationTypes.cloudxray.deleteStack) &&
        1000;

      Modal.confirm({
        width: modalWidth,
        closable: true,
        icon: null,
        maskClosable: true,
        centered: true,
        cancelButtonProps: {
          onClick: async () => {
            handleCancel(item.title.toLowerCase() === notificationTypes.cloudxray.deleteStack);
          },
        },
        title:
          item.title.toLowerCase() === notificationTypes.cloudxray.start ? (
            <b>Cloudxray Scan Started</b>
          ) : item.title.toLowerCase() === notificationTypes.cloudxray.deleteStack ? (
            <b>Delete Cloudxray Instances</b>
          ) : (
            <b>Cloudxray Scan Completed</b>
          ),
        content:
          item.title.toLowerCase() === notificationTypes.cloudxray.start ? (
            <CXrayScanStartContent
              description={description}
              cloudAccountName={cloudAccountName}
              orgName={orgName}
              notificationContent={notificationContent}
              provider={provider}
            />
          ) : item.title.toLowerCase() === notificationTypes.cloudxray.deleteStack ? (
            <CXrayDeleteStack provider={provider} />
          ) : (
            <CXrayScanCompleteContent
              description={description}
              cloudAccountName={cloudAccountName}
              orgName={orgName}
              notificationContent={notificationContent}
            />
          ),
        okText:
          item.title.toLowerCase() === notificationTypes.cloudxray.start
            ? 'View instances'
            : item.title.toLowerCase() === notificationTypes.cloudxray.deleteStack
            ? `Go To Cloud Account`
            : 'View risks',
        cancelText:
          item.title.toLowerCase() === notificationTypes.cloudxray.deleteStack
            ? 'Troubleshoot'
            : 'Cancel',
        async onOk() {
          await handleCXrayNotifClick({
            notificationType: item.title.toLowerCase(),
            cloudAccount: notifOfCloudAccount,
            orgId,
            provider,
          });
        },
      });
      return;
    }
    if (item?.title && item?.title.toLowerCase().includes('view')) {
      const url = `cloud-view`;
      router.push(url);
      return;
    }
    onClick(item);
  };

  const handleCXrayNotifClick = async ({ notificationType, cloudAccount, orgId, provider }) => {
    await handleCloudAccountSwitching(cloudAccount);
    switch (notificationType) {
      case notificationTypes.cloudxray.deleteStack:
        router.push(`/setup/cloud-accounts/edit?cloudaccount=${cloudAccount.id}`);
        break;
      case notificationTypes.cloudxray.completed:
        // Example usage for redirectToRiskScreen:
        const appliedRiskFilters = {
          categories: ['osvulnerabilities', 'malware'],
        };
        const riskScreenRoute = 'risks';

        redirectToScreen(orgId, appliedRiskFilters, defaultRiskFilters, riskScreenRoute);
        break;
      case notificationTypes.cloudxray.start:
        // Example usage for redirectToInventoryScreen:
        const appliedInventoryFilters = {
          services: {
            Compute: ['EC2:Instance'],
          },
          cloudAccounts: [cloudAccount],
        };
        // If the account scans only running instances add running filter too
        if (cloudAccount?.cloudXrayConfig?.scanRunningInstances) {
          appliedInventoryFilters.resourceStatus = ['running'];
        }
        const inventoryScreenRoute = 'inventory';

        redirectToScreen(
          orgId,
          appliedInventoryFilters,
          defaultInventoryFilters,
          inventoryScreenRoute
        );
        break;

      default:
        break;
    }
  };

  const handleCloudAccountSwitching = async switchToCloudAccount => {
    const { dispatch } = props;
    await dispatch({
      type: 'cloudAccount/clearSelected',
    });
    await dispatch({
      type: 'cloudAccount/addSelected',
      payload: switchToCloudAccount,
    });
  };

  const renderFailedResources = notification => {
    return (
      <div className={styles.popupResourceItem}>
        {notification?.resources?.map(resource => (
          <div className="border-bottom">
            <span style={{ marginBottom: 5, fontWeight: '500' }}>{resource?.name}</span>
            <div className="mt-2">
              <Tag>
                {notification && notification.risk && notification.risk.cloudAccount.name}
                {notification &&
                notification.risk &&
                notification.risk.signature.includes('AWS') ? (
                  <img src="/awslogo.png" alt="aws" style={{ height: 18, marginBottom: '3px' }} />
                ) : notification &&
                  notification.risk &&
                  notification.risk.signature.includes('AZ') ? (
                  <img src="/azure@2x.png" alt="azure" style={{ height: 12, marginLeft: 7 }} />
                ) : (
                  <img src="/gcplogo.png" alt="gcp" style={{ height: 20, marginRight: '-10px' }} />
                )}
              </Tag>
              <span style={{ fontWeight: 100, fontSize: 13 }}>
                {notification && notification.risk && notification.risk.service
                  ? notification.risk.service
                  : ''}
              </span>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // remove all script tags from the string and return the string
  const removeScriptTags = str => {
    return str.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
  };

  const getStartScanDescription = item => {
    if (item && item.provider && item.provider === 'GCP' && item.title === 'Cloudxray-start') {
      return `Scan Started on ${
        formatDateAndTime(item.notificationContent.startDate.toDate(), 'MMM DD, YYYY h:mm A')
          .exactTime
      } - Eastern Standard Time.`;
    } else {
      return item.description;
    }
  };

  const renderItem = (item, i) => {
    const itemCls = classNames(styles.item);
    // eslint-disable-next-line no-nested-ternary
    const leftIcon = item.avatar ? (
      typeof item.avatar === 'string' ? (
        <Avatar className={styles.avatar} src={item.avatar} />
      ) : (
        <span className={styles.iconElement}>{item.avatar}</span>
      )
    ) : null;
    return (
      <List.Item className={itemCls} key={item.key || i} onClick={() => handleItemClick(item)}>
        <List.Item.Meta
          className={styles.meta}
          avatar={leftIcon}
          title={
            <div className={styles.title}>
              {item.title === 'Market Place' || item.title === 'permission' || 'trial Expiry'
                ? ''
                : item?.title || ''}
              <div className={styles.extra}>{item.extra}</div>
            </div>
          }
          description={
            <div>
              {item.hasOwnProperty('risk') ? (
                <div style={{ display: 'inline-block', marginLeft: '13px' }}>
                  {
                    <Tooltip title="Cloud Account">
                      <Tag>
                        {item && item.risk && item.risk.cloudAccount.name}
                        {item && item.risk && item.risk.signature.includes('AWS') ? (
                          <img
                            src="/awslogo.png"
                            alt="aws"
                            style={{ height: 18, marginBottom: '3px' }}
                          />
                        ) : item && item.risk && item.risk.signature.includes('AZ') ? (
                          <img
                            src="/azure@2x.png"
                            alt="azure"
                            style={{ height: 12, marginLeft: 7 }}
                          />
                        ) : (
                          <img
                            src="/gcplogo.png"
                            alt="gcp"
                            style={{ height: 20, marginRight: '-10px' }}
                          />
                        )}
                      </Tag>
                    </Tooltip>
                  }
                </div>
              ) : /* Handle Eagle Eye Alerts */
              item && item.title && item.title.includes('Eagle Eye') ? (
                <div style={{ display: 'inline-block', marginLeft: '13px', marginBottom: '0px' }}>
                  {item.cloudAccountName && (
                    <Tag>
                      {item.cloudAccountName}
                      {item.provider === 'AWS' ? (
                        <img
                          src="/awslogo.png"
                          alt="aws"
                          style={{ height: 18, marginBottom: '3px' }}
                        />
                      ) : item.provider === 'AZURE' ? (
                        <img
                          src="/azure@2x.png"
                          alt="azure"
                          style={{ height: 12, marginLeft: 7 }}
                        />
                      ) : (
                        <img
                          src="/gcplogo.png"
                          alt="gcp"
                          style={{ height: 20, marginRight: '-10px' }}
                        />
                      )}
                    </Tag>
                  )}
                  <Tag color="orange">Eagle Eye</Tag>
                </div>
              ) : item && item.title && item.title.includes('Cloudxray') ? (
                <div style={{ display: 'inline-block', marginLeft: '13px', marginBottom: '0px' }}>
                  {item.cloudAccountName && (
                    <Tag>
                      {item.cloudAccountName}
                      {item && item.provider === 'AWS' ? (
                        <img
                          src="/awslogo.png"
                          alt="aws"
                          style={{ height: 18, marginBottom: '3px' }}
                        />
                      ) : item && item.provider === 'AZURE' ? (
                        <img
                          src="/azure@2x.png"
                          alt="azure"
                          style={{ height: 12, marginLeft: 7 }}
                        />
                      ) : (
                        <img
                          src="/gcplogo.png"
                          alt="gcp"
                          style={{ height: 20, marginRight: '-10px' }}
                        />
                      )}
                    </Tag>
                  )}

                  {item.title === 'Cloudxray-start' ? (
                    <Tag color="blue">Cloudxray scan started</Tag>
                  ) : item.title === 'Delete Cloudxray Scanning Instances' ? (
                    <Tag color="red">Delete Cloudxray Instances</Tag>
                  ) : (
                    <Tag color="green">Cloudxray scan completed</Tag>
                  )}
                </div>
              ) : item && item.title && item.title.toLowerCase().includes('view') ? (
                <div style={{ display: 'inline-block', marginLeft: '13px', marginBottom: '0px' }}>
                  {item.cloudAccountName && (
                    <Tag>
                      {item.cloudAccountName}
                      {item && item.provider === 'AWS' ? (
                        <img
                          src="/awslogo.png"
                          alt="aws"
                          style={{ height: 18, marginBottom: '3px' }}
                        />
                      ) : item && item.provider === 'AZURE' ? (
                        <img
                          src="/azure@2x.png"
                          alt="azure"
                          style={{ height: 12, marginLeft: 7 }}
                        />
                      ) : (
                        <img
                          src="/gcplogo.png"
                          alt="gcp"
                          style={{ height: 20, marginRight: '-10px' }}
                        />
                      )}
                    </Tag>
                  )}

                  {item.title.toLowerCase().includes('started') ? (
                    <Tag color="orange">Cloud View scan started</Tag>
                  ) : item.title.toLowerCase().includes('progress') ? (
                    <Tag color="blue">Cloud View scan is in progress</Tag>
                  ) : (
                    <Tag color="green">Cloud View scan completed</Tag>
                  )}
                </div>
              ) : item &&
                item.title &&
                item.title.toLowerCase() === 'force cleanup ended successfully' ? (
                <div style={{ display: 'inline-block', marginLeft: '13px', marginBottom: '0px' }}>
                  {item.cloudAccountName && (
                    <Tag>
                      {item.cloudAccountName}
                      {item && item.provider === 'AWS' ? (
                        <img
                          src="/awslogo.png"
                          alt="aws"
                          style={{ height: 18, marginBottom: '3px' }}
                        />
                      ) : item && item.provider === 'AZURE' ? (
                        <img
                          src="/azure@2x.png"
                          alt="azure"
                          style={{ height: 12, marginLeft: 7 }}
                        />
                      ) : (
                        <img
                          src="/gcplogo.png"
                          alt="gcp"
                          style={{ height: 20, marginRight: '-10px' }}
                        />
                      )}
                    </Tag>
                  )}

                  {item.title.toLowerCase().includes('successful') ? (
                    <Tag color="green">{item.title}</Tag>
                  ) : (
                    <Tag color="orange">Something went wrong with the force cleanup</Tag>
                  )}
                </div>
              ) : item && item.title && item.title === 'Market Place' ? (
                <div style={{ display: 'inline-block', marginLeft: '13px', marginBottom: '0px' }}>
                  {<Tag color="green">AWS Market Place</Tag>}
                </div>
              ) : item && item.title && item.title === 'permission' ? (
                <div style={{ display: 'inline-block', marginLeft: '13px', marginBottom: '0px' }}>
                  {item.cloudAccountName && (
                    <Tag>
                      {item.cloudAccountName}
                      {item && item.provider === 'AWS' ? (
                        <img
                          src="/awslogo.png"
                          alt="aws"
                          style={{ height: 18, marginBottom: '3px' }}
                        />
                      ) : item && item.provider === 'AZURE' ? (
                        <img
                          src="/azure@2x.png"
                          alt="azure"
                          style={{ height: 12, marginLeft: 7 }}
                        />
                      ) : (
                        <img
                          src="/gcplogo.png"
                          alt="gcp"
                          style={{ height: 20, marginRight: '-10px' }}
                        />
                      )}
                    </Tag>
                  )}

                  {<Tag color="red">Permission issue</Tag>}
                </div>
              ) : item && item.title && item.title === 'trial Expiry' ? (
                <div style={{ display: 'inline-block', marginLeft: '13px', marginBottom: '0px' }}>
                  {<Tag color="green">Trial Expired</Tag>}
                </div>
              ) : (
                <div style={{ display: 'inline-block', marginLeft: '13px', marginBottom: '0px' }}>
                  {<Tag color="green">Invitation</Tag>}
                </div>
              )}
              <div style={{ display: 'inline-block', float: 'right', marginRight: '-8px' }}>
                {item && item.risk && item.risk.status === 'fail' ? (
                  <CloseCircleOutlined
                    style={{ color: '#F4657C', fontSize: 20, marginRight: '8px' }}
                  />
                ) : item && item.risk && item.risk.status === 'pass' ? (
                  <CheckCircleOutlined
                    style={{ color: '#3ADFA3', fontSize: 20, marginRight: '8px' }}
                  />
                ) : (
                  ''
                )}
              </div>
              <div className={styles.risk}>
                <RenderDot
                  color={
                    (item && item.risk && item.risk.severity.toLowerCase() === 'high') ||
                    (item && item.title && item.title === 'permission') ||
                    (item && item.title && item.title === 'Cloudxray')
                      ? '#E84A58'
                      : item && item.risk && item.risk.severity.toLowerCase() === 'medium'
                      ? '#FCDB6B'
                      : item && item.risk && item.risk.severity.toLowerCase() === 'low'
                      ? '#2893FC'
                      : ''
                  }
                />
              </div>
              {item.description && (
                <div className={styles.description}>
                  <Linkify
                    options={{
                      target: {
                        url: '_blank',
                      },
                    }}
                  >
                    <div
                      className="content"
                      dangerouslySetInnerHTML={{
                        __html: removeScriptTags(getStartScanDescription(item)),
                      }}
                    />
                  </Linkify>
                </div>
              )}
              {item && item.hasOwnProperty('impactedResources') && (
                <div style={{ marginLeft: 13 }}>
                  Failing on:{' '}
                  {item?.resources ? (
                    <Popover
                      zIndex={2}
                      destroyTooltipOnHide={true}
                      placement="left"
                      content={renderFailedResources(item)}
                      title="Failed Resources"
                    >
                      <b style={{ color: '#1890FF', cursor: 'pointer' }}>
                        {item.impactedResources} Resource(s)
                      </b>
                    </Popover>
                  ) : (
                    <b style={{ color: '#1890FF', cursor: 'pointer' }}>
                      {item.impactedResources} Resource(s)
                    </b>
                  )}
                </div>
              )}{' '}
              <div className={styles.datetime}>
                {' '}
                {item && item.risk && item.risk.signature ? '' : 'Organization:'}
                <span style={{ fontWeight: 500 }}>
                  {' '}
                  {item && item.risk && item.risk.signature
                    ? item.risk.signature
                    : item && item.organization && item.organization.name}
                </span>
                <span style={{ fontSize: '12px', float: 'right' }}>
                  {item && item.createdAt && moment(new Date(item.createdAt.toDate())).fromNow()}
                </span>
              </div>
            </div>
          }
        />
      </List.Item>
    );
  };

  return (
    <List
      loadMore={
        <div
          style={{
            textAlign: 'center',
            lineHeight: '32px',
          }}
        >
          {!loadingMore ? (
            <div
              style={{
                color: !lastVisible ? 'grey' : '#228FFD',
                height: '35px',
                cursor: !lastVisible ? 'not-allowed' : 'pointer',
              }}
              onClick={handleInfiniteOnLoad}
            >
              {lastVisible ? 'View more' : 'No more notifications'}
            </div>
          ) : (
            <Spin />
          )}
        </div>
      }
      dataSource={data}
      className={styles.list}
      renderItem={renderItem}
    >
      <div className={styles.bottomBar}>
        {showClear ? (
          <div onClick={onClear}>
            {locale.clear} {locale[title] || title}
          </div>
        ) : null}
        {showViewMore ? <div onClick={onViewMore}>{locale.viewMore}</div> : null}
      </div>

      <Modal
        title="System Notification"
        footer={null}
        visible={isDetailsModalVisible}
        onCancel={() => setIsDetailsModalVisible(false)}
      >
        <div className={styles.list}>{currentModalItem && renderItem(currentModalItem)}</div>
      </Modal>
    </List>
  );
};

const CXrayScanStartContent = ({
  cloudAccountName,
  description,
  notificationContent,
  provider,
}) => {
  if (!notificationContent)
    return (
      <p
        dangerouslySetInnerHTML={{
          __html: description,
        }}
      />
    );
  const { instanceCount, startDate } = notificationContent || {};

  return (
    <div>
      Cloudnosys has began scanning the <b>{cloudAccountName}</b> cloud account for Malware & OS
      Vulnerabilities.
      <br />
      <br />
      {provider === 'AWS' ? (
        <>
          A total of <b>{instanceCount}</b> Instance(s) have started being scanned on{' '}
          {formatDateAndTime(startDate.toDate(), 'MMM DD, YYYY h:mm A').exactTime} - Eastern
          Standard Time.
        </>
      ) : (
        <>
          Scan Started on {formatDateAndTime(startDate.toDate(), 'MMM DD, YYYY h:mm A').exactTime} -
          Eastern Standard Time.
        </>
      )}
    </div>
  );
};

const CXrayScanCompleteContent = ({ cloudAccountName, notificationContent, description }) => {
  if (!notificationContent)
    return (
      <p
        dangerouslySetInnerHTML={{
          __html: description,
        }}
      />
    );
  const {
    totalCVEs,
    totalMalwares,
    instanceCount,
    cxrayInstanceFailureCount,
    startDate,
    endDate,
    failedInstances = [],
  } = notificationContent || {};

  const failureReasonMap = [];
  failedInstances.forEach(item => {
    const { reason: cloudXrayFailureReason, id: instanceId } = item;

    if (!failureReasonMap[cloudXrayFailureReason]) {
      failureReasonMap[cloudXrayFailureReason] = [];
    }
    failureReasonMap[cloudXrayFailureReason].push(instanceId);
  });

  const failureReasonAndInstances = Object.entries(failureReasonMap).map(([errorName, ids]) => ({
    errorName,
    ids,
  }));

  return (
    <div>
      Cloudnosys just finished scanning the <b>{cloudAccountName}</b> cloud account for Malwares &
      OS Vulnerabilities.
      <br />
      <br />
      In total, <b>{totalCVEs}</b> CVE(s) and <b>{totalMalwares}</b> Malware(s) were detected.
      <br />
      <br />A total of <b>{instanceCount}</b> instances were discovered in the cloud account{' '}
      {cxrayInstanceFailureCount > 0 && (
        <span>
          out of which <b>{cxrayInstanceFailureCount}</b> instances could not be scanned.
        </span>
      )}
      <br />
      <br />
      The scan started on {
        formatDateAndTime(startDate.toDate(), 'MMM DD, YYYY h:mm A').exactTime
      }{' '}
      and ended on {formatDateAndTime(endDate.toDate(), 'MMM DD, YYYY h:mm A').exactTime} - Eastern
      Standard Time.
      <br /> <br />
      {failedInstances.length > 0 ? (
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <table cellSpacing="2" style={{ borderCollapse: 'collapse', width: '100%' }} id="table">
            <thead>
              <tr>
                {/* Center aligned headings */}
                <th style={{ textAlign: 'left', padding: '1px 10px 1px 10px' }}>
                  <p className="table_heading">Failure Reason</p>
                </th>
                <th style={{ textAlign: 'left', padding: '1px 10px 1px 10px' }}>
                  <p className="table_heading">Instance Ids</p>
                </th>
              </tr>
            </thead>
            <tbody>
              {failureReasonAndInstances?.map((item, index) => (
                <tr
                  key={index}
                  style={{
                    backgroundColor: '#F9F9F9',
                    borderBottom: '6pt solid rgb(255, 255, 255)',
                  }}
                >
                  <td style={{ padding: '2px 10px', textAlign: 'left' }}>
                    <p className="risk_paragraph">{item.errorName}</p>
                  </td>
                  <td style={{ padding: '2px 10px', textAlign: 'left' }}>{item.ids.join(', ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

const CXrayDeleteStack = ({ provider }) => {
  return (
    <>
      {provider.toLowerCase() == 'azure' && (
        <div>
          The automatic cleanup function of the Cloudxray Scan feature did not execute as expected
          due to an unforeseen issue. To prevent you from incurring unnecessary costs, please
          manually delete the following Azure resources{' '}
          <b>(disk, snapshots, and virtual machines)</b> by pressing the force clean-up button
        </div>
      )}

      {provider?.toLowerCase() == 'aws' && (
        <div>
          The automatic cleanup function of the Cloudxray Scan feature did not execute as expected
          due to an unforeseen issue. To prevent you from incurring unnecessary costs, please
          manually delete the following AWS resources{' '}
          <b>(AMIs, snapshots, volumes, instances, and Orchestrator)</b> by pressing the force
          clean-up button.
        </div>
      )}

      {provider?.toLowerCase() == 'gcp' && (
        <div>
          The automatic cleanup function of the Cloudxray Scan feature did not execute as expected
          due to an unforeseen issue. To prevent you from incurring unnecessary costs, please
          manually delete the following GCP resources <b>(compute images, snapshots, and disks)</b>{' '}
          by pressing the force clean-up button:
        </div>
      )}
    </>
  );
};

// @connect is from dva
const mapStateToProps = ({ cloudAccount }) => ({
  allCloudAccountsList: cloudAccount.allCloudAccountsList,
});

export default connect(mapStateToProps)(NoticeList);
